import {
  useStytchB2BClient,
  useStytchMember,
  useStytchMemberSession,
  useStytchOrganization,
  type Member,
  type MemberSession,
  type Organization,
} from '@stytch/react/b2b';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { createContext, useEffect, useMemo, type PropsWithChildren } from 'react';

import type { UserInstanceType } from '@/api/organization';
import { type UserProfileType } from '@/api/user';
import { sessionDurationMinutes } from '@/constants/numbers';
import { updateUserContext } from '@/helper/launchdarkly';
import { updatePendoUser } from '@/helper/pendo';
import { updateSentryTags } from '@/helper/sentry';

import { type EnhancedMember, type UserContextType } from './types';

export const UserContext = createContext<UserContextType | undefined>(undefined);

const extractInstanceFromPath = (path: string) => {
  const match = path.match(/^\/app\/inst\/([^/]+)/);
  return match ? match[1] : null;
};

export const createUserContext = (
  member: Member,
  session: MemberSession,
  organization: Organization,
) => {
  const instanceId = organization.organization_slug.replace(/^inst~/, 'inst:');

  const roles = member.roles.map((role) => role.role_id);
  const isC99Superuser = roles.some((role: string) => role === 'c99.role.superuser');

  const currentOrg: UserInstanceType = {
    id: organization.organization_id,
    name: organization.organization_name,
    slug: organization.organization_slug,
    instanceId,
    isForeign: false,
    homeInstance: instanceId,
  };

  let pictureUrl = null;
  const registrationWithPicture = ((member as EnhancedMember)?.oauth_registrations ?? []).find(
    (registration) => registration.profile_picture_url != null,
  );
  if (registrationWithPicture) {
    pictureUrl = registrationWithPicture?.profile_picture_url;
  }

  return {
    id: `user:${member.email_address.replaceAll('.', '~')}`,
    stytchId: member.member_id,
    email: member.email_address,
    name: member.name,
    authFactors: session.authentication_factors.map((factor) => ({
      type: factor.type,
      deliveryMethod: factor.delivery_method,
    })),
    pictureUrl,
    roles,
    currentOrg,
    isC99Superuser,
    isAdmin: roles.some((role: string) => role === 'c99.role.admin'),
    // TODO: Setting `isPaid` to true for now. We intend to eventually rework roles/permissions
    isPaid: true,
    status: member.status,
  } as UserProfileType;
};

export const UserProvider = ({ children }: PropsWithChildren) => {
  const stytch = useStytchB2BClient();
  const { member } = useStytchMember();
  const { session } = useStytchMemberSession();
  const { organization } = useStytchOrganization();
  const ldClient = useLDClient();

  const urlInstance = extractInstanceFromPath(window.location.pathname);

  const user = useMemo(() => {
    if (member && organization && session) {
      const userContext = createUserContext(member, session, organization);
      const isForeignInstance =
        urlInstance && `inst:${urlInstance}` !== userContext.currentOrg.homeInstance;
      if (isForeignInstance && userContext.isC99Superuser) {
        userContext.currentOrg = {
          id: 'foreign-instance',
          name: urlInstance,
          slug: `inst~${urlInstance}`,
          instanceId: `inst:${urlInstance}`,
          isForeign: true,
          homeInstance: userContext.currentOrg.homeInstance,
        };
      }
      return userContext;
    } else {
      return undefined;
    }
  }, [member, session, organization, urlInstance]);

  useEffect(() => {
    if (user) {
      // if user changes, update LD, Pendo, and Sentry contexts
      updateUserContext(user, ldClient);
      updatePendoUser(user);
      updateSentryTags(user);

      // Extend the session
      stytch.session.authenticate({
        session_duration_minutes: sessionDurationMinutes,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stytch, ldClient, user?.id, urlInstance]);

  return <UserContext.Provider value={{ user }}>{children}</UserContext.Provider>;
};
